import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { ownersApiService } from "@services/apis/Owners";
import {
  isOwnersApiFailure,
  Owner,
} from "@services/models/Owners/OwnersModels";
import React, { useMemo, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface UpdateOwnerContactSliderProps {
  isOpen: boolean;
  owner: Owner | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const UpdateOwnerContactSlider: React.FC<UpdateOwnerContactSliderProps> = ({
  isOpen,
  owner,
  onClose,
  onSuccess,
}) => {
  const { handleError, handleSuccess } = useSnackbarMessage();

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const canSubmit = useMemo(
    () => !!owner?.ownerId && (email.trim().length > 0 || phone.trim().length > 0),
    [owner?.ownerId, email, phone],
  );

  const resetForm = () => {
    setEmail("");
    setPhone("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!owner?.ownerId) {
      handleError({ message: "ownerId is required" });
      return;
    }
    if (!canSubmit) {
      handleError({ message: "At least one of email or phone is required" });
      return;
    }

    try {
      setIsLoading(true);
      const res = await ownersApiService.updateOwnerContact({
        ownerId: owner.ownerId,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      if (isOwnersApiFailure(res)) {
        handleError({ message: res.msg || "Failed to update owner contact" });
        return;
      }
      handleSuccess({ message: res.msg || "Owner contact updated" });
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      handleError({
        message: err?.response?.data?.msg || "Failed to update owner contact",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const heading = owner
    ? `Update Contact — ${owner.displayName}`
    : "Update Owner Contact";

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={heading}
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
    >
      <div className="flex flex-col px-[16px] py-[16px] gap-4">
        <GlobalTextInputField
          title="Email (optional)"
          placeholder={owner?.email || "owner@company.com"}
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Phone (optional)"
          placeholder={owner?.phone || "+1 555 123 4567"}
          value={phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPhone(e.target.value)
          }
        />
        <CustomButton
          text="UPDATE CONTACT"
          onClick={handleSubmit}
          isActive={canSubmit}
          isButtonLoading={isLoading}
        />
      </div>
    </SlidingPanel>
  );
};

export default UpdateOwnerContactSlider;
